import { useCallback, useEffect, useState } from 'react';
import { CalendarDays, Camera, CheckCircle2, Clock, Gauge, MapPin, Package, Trophy, Truck, Users, Wrench } from 'lucide-react';
import { formatDateTime, formatDay } from '@nu/shared';
import { api } from '../api/client.js';
import RouteMap from '../components/RouteMap.jsx';
import { problem } from '../modules/bookings/util.js';

// My profile, for everyone: who they are, their team today, their home
// postcode (where their van starts and ends the day in Plan the day) to
// check and confirm, upcoming leave from BrightHR, and their numbers from
// their team's jobs: installs, time on site, photos, stock used.
const KIND = { holiday: 'Holiday', sickness: 'Sick', toil: 'TOIL', other: 'Away' };
const PART = { all: '', am: ' (AM)', pm: ' (PM)' };
// A confirmation older than this asks again.
const STALE_DAYS = 90;

const hoursMins = (m) => (m == null ? '–' : m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`);
const initials = (name = '') => name.trim().split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

function Stat({ icon: Icon, value, label }) {
  return (
    <div className="stat profile-stat">
      <Icon size={18} aria-hidden="true" className="profile-stat-icon" />
      <span className="stat-num">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

function HomePostcode({ person, onSaved }) {
  const [value, setValue] = useState(person.homePostcode || '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => { setValue(person.homePostcode || ''); }, [person.homePostcode]);
  const stale = !person.confirmedAt || Date.now() - new Date(person.confirmedAt) > STALE_DAYS * 86_400_000;
  const typed = value.trim().toUpperCase().replace(/\s+/g, '');
  const same = typed === (person.homePostcode || '').replace(/\s+/g, '');
  const save = async (postcode) => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api('/profile/postcode', { method: 'PUT', body: { postcode } });
      setMsg({ ok: true, text: r.changed ? `Saved: your home postcode is now ${r.homePostcode}.` : `Thanks, ${r.homePostcode} is confirmed.` });
      onSaved(r);
    } catch (e) {
      setMsg({ ok: false, text: problem(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card">
      <h2 className="with-icon"><MapPin size={20} aria-hidden="true" /> Home postcode</h2>
      <p className="muted">Where your van starts and ends the day. Plan the day uses it to put your jobs in order, so keep it right. Postcode only, not your address.</p>
      {person.homePostcode && stale && <p className="notice-line notice-warn">{person.confirmedAt ? `Last confirmed ${formatDay(person.confirmedAt)}. ` : ''}Is {person.homePostcode} still right?</p>}
      {!person.homePostcode && <p className="notice-line notice-warn">You haven&apos;t got a home postcode yet. Add it below.</p>}
      <form className="profile-postcode" onSubmit={(e) => { e.preventDefault(); if (typed) save(value); }}>
        <input value={value} onChange={(e) => setValue(e.target.value.toUpperCase())} maxLength={8} placeholder="e.g. LE12 9LY" aria-label="Home postcode" autoComplete="postal-code" />
        {person.homePostcode && same
          ? <button type="submit" className="btn primary with-icon" disabled={busy}><CheckCircle2 size={18} aria-hidden="true" /> Yes, that&apos;s right</button>
          : <button type="submit" className="btn primary" disabled={busy || !typed}>Save postcode</button>}
      </form>
      {msg && <p className={`notice-line ${msg.ok ? 'notice-ok' : 'notice-bad'}`}>{msg.text}</p>}
      {person.confirmedAt && !stale && <p className="small muted">Confirmed {formatDateTime(person.confirmedAt)}.</p>}
      {person.place && (
        <RouteMap height={200} simple stops={[{ key: 'home', coord: [person.place.lng, person.place.lat], label: '⌂', colour: '#0B2340', title: person.homePostcode }]} />
      )}
    </section>
  );
}

// Installs a week for the last 8 weeks, as bars.
function Weeks({ weeks }) {
  const top = Math.max(1, ...weeks.map((w) => w.completed));
  return (
    <ol className="profile-weeks" aria-label="Installs completed each week">
      {weeks.map((w, i) => (
        <li key={w.week} className={i === weeks.length - 1 ? 'now' : ''} title={`Week of ${formatDay(w.week)}: ${w.completed}`}>
          <span className="profile-bar-n">{w.completed}</span>
          <span className="profile-bar" style={{ height: `${Math.max(4, (w.completed / top) * 100)}%` }} />
          <span className="profile-bar-label">{i === weeks.length - 1 ? 'This wk' : formatDay(w.week).slice(0, 5)}</span>
        </li>
      ))}
    </ol>
  );
}

export default function Profile({ me }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [allStock, setAllStock] = useState(false);
  const load = useCallback(() => api('/profile').then((d) => { setData(d); setError(null); }, (e) => setError(problem(e))), []);
  useEffect(() => { load(); }, [load]);
  const p = data?.person;
  const s = data?.stats;
  const stock = s ? (allStock ? s.stock : s.stock.slice(0, 8)) : [];

  return (
    <div className="stack profile-page">
      <section className="card profile-head">
        <span className="avatar profile-avatar" aria-hidden="true">{initials(me.name)}</span>
        <div>
          <h1>{me.name}</h1>
          <p className="muted">{me.email}</p>
          {p && (
            <p className="profile-facts">
              <span className="tag">{p.roleLabel}</span>
              {p.team && <span className="with-icon"><Users size={16} aria-hidden="true" /> {p.team.name}{p.team.with.length ? ` with ${p.team.with.join(' and ')}` : ''}</span>}
              {p.vanReg && <span className="with-icon"><Truck size={16} aria-hidden="true" /> {p.vanReg}</span>}
            </p>
          )}
        </div>
      </section>

      {error && <section className="card"><p className="notice-line notice-bad">{error}</p></section>}
      {!data && !error && <section className="card"><p className="muted">Loading…</p></section>}
      {data && !p && (
        <section className="card">
          <p className="muted">You&apos;re not on the Match-Ups list, so there&apos;s no home postcode or job numbers to show. If you go out on jobs, ask the office to add you.</p>
        </section>
      )}

      {p && <HomePostcode person={p} onSaved={(r) => setData((d) => ({ ...d, person: { ...d.person, homePostcode: r.homePostcode, confirmedAt: r.confirmedAt, place: r.place } }))} />}

      {s && (
        <section className="card">
          <h2 className="with-icon"><Gauge size={20} aria-hidden="true" /> Your numbers</h2>
          <p className="muted small">From your team&apos;s jobs on the days you were in it{s.firstJob ? `, since ${formatDay(s.firstJob)}` : ''}.</p>
          <div className="stat-row profile-stats">
            <Stat icon={CheckCircle2} value={s.completed} label="Installs completed" />
            <Stat icon={CalendarDays} value={s.thisWeek} label={`This week${s.today ? ` (${s.today} today)` : ''}`} />
            <Stat icon={CalendarDays} value={s.thisMonth} label="This month" />
            <Stat icon={Wrench} value={s.completionRate == null ? '–' : `${s.completionRate}%`} label={`Completion rate${s.notDone ? ` · ${s.notDone} not done` : ''}`} />
            <Stat icon={Clock} value={hoursMins(s.avgOnSiteMins)} label="Average time on site" />
            <Stat icon={Trophy} value={s.bestDay ? s.bestDay.completed : '–'} label={s.bestDay ? `Best day: ${formatDay(s.bestDay.day)}` : 'Best day'} />
            <Stat icon={Camera} value={s.photos} label="Photos taken" />
            <Stat icon={Package} value={s.metersFitted} label="Meters fitted" />
          </div>
          {s.completed > 0 && (
            <>
              <h3>Installs each week</h3>
              <Weeks weeks={s.weeks} />
            </>
          )}
          {p.dailyTarget != null && <p className="small muted">Daily target{p.band ? ` (${p.band} band)` : ''}: {p.dailyTarget} installs per engineer. Worked {s.daysWorked} day{s.daysWorked === 1 ? '' : 's'} with installs.</p>}
        </section>
      )}

      {s?.stock.length > 0 && (
        <section className="card">
          <h2 className="with-icon"><Package size={20} aria-hidden="true" /> Stock used</h2>
          <p className="muted small">Logged on your team&apos;s completed jobs, most used first.</p>
          <table className="table profile-stock">
            <thead><tr><th>Item</th><th className="num">Used</th></tr></thead>
            <tbody>
              {stock.map((i) => (
                <tr key={i.sku}><td>{i.description}<span className="small muted"> · {i.sku}</span></td><td className="num">{i.qty}</td></tr>
              ))}
            </tbody>
          </table>
          {s.stock.length > 8 && (
            <button type="button" className="link-btn" onClick={() => setAllStock((x) => !x)}>{allStock ? 'Show fewer' : `Show all ${s.stock.length}`}</button>
          )}
        </section>
      )}

      {p && (
        <section className="card">
          <h2 className="with-icon"><CalendarDays size={20} aria-hidden="true" /> Coming up off</h2>
          {data.leave.length
            ? (
              <ul className="history">
                {data.leave.map((l) => (
                  <li key={l.from}>
                    <strong>{l.label || KIND[l.kind] || 'Off'}{PART[l.part] || ''}</strong>
                    <span className="small muted">{l.from === l.to ? formatDay(l.from) : `${formatDay(l.from)} to ${formatDay(l.to)}`}</span>
                  </li>
                ))}
              </ul>
            )
            : <p className="muted">Nothing booked in BrightHR for the next 60 days.</p>}
        </section>
      )}
    </div>
  );
}
